import { useState, useRef, useEffect, useCallback } from "react";
import appointmentVideo from "../assets/appointment-video.mp4";
import {
  motion,
  useSpring,
  AnimatePresence,
  useScroll,
  useTransform,
  useInView,
  useMotionValue,
} from "framer-motion";
// import HomeAuroraBackground from "@/components/HomeAuroraBackground";
import { FaCheck, FaPlay, FaPause } from "react-icons/fa";

const F = "'Poppins', sans-serif";
const MONO = "'JetBrains Mono', monospace";

// ── Floating Particle ──────────────────────────────────────────────────────────
function Particle({ style }) {
  return (
    <motion.div
      style={{ position: "absolute", borderRadius: "50%", pointerEvents: "none", ...style }}
      animate={{ y: [0, -30, 0], opacity: [0.15, 0.5, 0.15], scale: [1, 1.3, 1] }}
      transition={{
        duration: style.duration || 4,
        repeat: Infinity,
        delay: style.delay || 0,
        ease: "easeInOut",
      }}
    />
  );
}

// ── DESKTOP: Full Step Progress Pills (unchanged) ──────────────────────────────
function StepPill({ steps, current, isVisible }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        flexWrap: "wrap",
        justifyContent: "center",
      }}>
      {steps.map((s, i) => (
        <motion.div
          key={i}
          style={{ display: "flex", alignItems: "center", gap: 10 }}
          initial={{ opacity: 0, y: 20, scale: 0.85 }}
          animate={isVisible ? { opacity: 1, y: 0, scale: 1 } : { opacity: 0, y: 20, scale: 0.85 }}
          transition={{ duration: 0.5, delay: 0.6 + i * 0.12, ease: [0.22, 1, 0.36, 1] }}>
          <motion.div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              padding: "10px 20px",
              borderRadius: 50,
              background:
                i === current
                  ? "rgba(0,214,143,0.22)"
                  : i < current
                    ? "rgba(10,80,64,0.35)"
                    : "rgba(15,23,42,0.72)",
              border: `1.5px solid ${
                i === current
                  ? "#00d68f"
                  : i < current
                    ? "rgba(0,214,143,0.4)"
                    : "rgba(148,163,184,0.32)"
              }`,
              boxShadow: i === current ? "0 0 18px rgba(0,214,143,0.25)" : "none",
              transition: "all 0.4s ease",
            }}
            whileHover={{ scale: 1.05 }}>
            <motion.div
              style={{
                width: 26,
                height: 26,
                borderRadius: "50%",
                background:
                  i < current
                    ? "#00d68f"
                    : i === current
                      ? "rgba(0,214,143,0.25)"
                      : "rgba(15,23,42,0.9)",
                border: `2px solid ${
                  i === current ? "#00d68f" : i < current ? "#00d68f" : "rgba(148,163,184,0.42)"
                }`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 11,
                color: i < current ? "#06351f" : i === current ? "#bbffe5" : "#cbd5e1",
                fontWeight: 800,
                flexShrink: 0,
              }}
              animate={
                i === current
                  ? {
                      scale: [1, 1.15, 1],
                      boxShadow: [
                        "0 0 0px rgba(0,214,143,0)",
                        "0 0 12px rgba(0,214,143,0.6)",
                        "0 0 0px rgba(0,214,143,0)",
                      ],
                    }
                  : {}
              }
              transition={{ duration: 1.5, repeat: Infinity }}>
              {i < current ? <FaCheck size={10} /> : i + 1}
            </motion.div>
            <span
              style={{
                fontSize: 13,
                fontFamily: F,
                letterSpacing: "0.01em",
                whiteSpace: "nowrap",
                color: i === current ? "#36f2ab" : i < current ? "#86efc0" : "#cbd5e1",
                fontWeight: i === current ? 700 : 500,
              }}>
              {s}
            </span>
          </motion.div>

          {i < steps.length - 1 && (
            <motion.div
              style={{
                width: 28,
                height: 2,
                borderRadius: 2,
                originX: 0,
                background:
                  i < current
                    ? "linear-gradient(90deg,#00d68f,rgba(0,214,143,0.3))"
                    : "rgba(148,163,184,0.28)",
                transition: "background 0.4s ease",
              }}
              initial={{ scaleX: 0 }}
              animate={isVisible ? { scaleX: 1 } : { scaleX: 0 }}
              transition={{ duration: 0.4, delay: 0.8 + i * 0.12 }}
            />
          )}
        </motion.div>
      ))}
    </div>
  );
}

// ── MOBILE: Single Active Step Display ────────────────────────────────────────
function MobileStepDisplay({ steps, current, isVisible }) {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 12,
        width: "100%",
      }}>
      {/* Dot progress track */}
      <motion.div
        style={{ display: "flex", alignItems: "center", gap: 8 }}
        initial={{ opacity: 0 }}
        animate={isVisible ? { opacity: 1 } : { opacity: 0 }}
        transition={{ duration: 0.5, delay: 0.4 }}>
        {steps.map((_, i) => (
          <motion.div
            key={i}
            style={{
              borderRadius: 99,
              background:
                i === current
                  ? "#00d68f"
                  : i < current
                    ? "rgba(0,214,143,0.5)"
                    : "rgba(148,163,184,0.25)",
              height: 6,
              flexShrink: 0,
              originX: 0.5,
            }}
            animate={{
              width: i === current ? 28 : 6,
              boxShadow: i === current ? "0 0 10px rgba(0,214,143,0.7)" : "none",
            }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
          />
        ))}
      </motion.div>

      {/* Active step card — animates in/out on change */}
      <div
        style={{
          position: "relative",
          width: "100%",
          maxWidth: 320,
          minHeight: 52,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}>
        <AnimatePresence mode="wait">
          <motion.div
            key={current}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              padding: "12px 20px",
              borderRadius: 50,
              background: "rgba(0,214,143,0.15)",
              border: "1.5px solid rgba(0,214,143,0.55)",
              boxShadow: "0 0 24px rgba(0,214,143,0.2)",
              width: "100%",
              justifyContent: "center",
            }}
            initial={{ opacity: 0, y: 12, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -12, scale: 0.9 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}>
            {/* Number badge */}
            <motion.div
              style={{
                width: 30,
                height: 30,
                borderRadius: "50%",
                background: "rgba(0,214,143,0.25)",
                border: "2px solid #00d68f",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 12,
                color: "#bbffe5",
                fontWeight: 800,
                flexShrink: 0,
                fontFamily: F,
              }}
              animate={{
                scale: [1, 1.15, 1],
                boxShadow: [
                  "0 0 0px rgba(0,214,143,0)",
                  "0 0 14px rgba(0,214,143,0.65)",
                  "0 0 0px rgba(0,214,143,0)",
                ],
              }}
              transition={{ duration: 1.5, repeat: Infinity }}>
              {current + 1}
            </motion.div>

            {/* Step label */}
            <span
              style={{
                fontSize: 15,
                fontFamily: F,
                letterSpacing: "0.01em",
                color: "#36f2ab",
                fontWeight: 700,
                whiteSpace: "nowrap",
              }}>
              {steps[current]}
            </span>

            {/* Step counter */}
            <span
              style={{
                fontSize: 10,
                fontFamily: MONO,
                color: "rgba(0,214,143,0.45)",
                letterSpacing: "0.08em",
                marginLeft: 2,
                whiteSpace: "nowrap",
              }}>
              {current + 1}/{steps.length}
            </span>
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Completed steps hint */}
      {current > 0 && (
        <motion.div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            opacity: 0.5,
          }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.5 }}
          transition={{ duration: 0.3 }}>
          {Array.from({ length: current }, (_, i) => (
            <span
              key={i}
              style={{
                fontSize: 10,
                fontFamily: F,
                color: "#86efc0",
                display: "flex",
                alignItems: "center",
                gap: 3,
              }}>
              <FaCheck size={7} color="#00d68f" />
              {steps[i]}
              {i < current - 1 && (
                <span style={{ color: "rgba(148,163,184,0.4)", marginLeft: 3 }}>·</span>
              )}
            </span>
          ))}
        </motion.div>
      )}
    </div>
  );
}

// ── Custom Video Cursor ────────────────────────────────────────────────────────
function VideoCursor({ mouseX, mouseY, isHovering, isPlaying }) {
  const springConfig = { stiffness: 600, damping: 45, mass: 0.5 };
  const smoothX = useSpring(mouseX, springConfig);
  const smoothY = useSpring(mouseY, springConfig);

  return (
    <AnimatePresence>
      {isHovering && (
        <motion.div
          style={{
            position: "fixed",
            left: smoothX,
            top: smoothY,
            pointerEvents: "none",
            zIndex: 9999,
            x: "-50%",
            y: "-50%",
          }}
          initial={{ scale: 0.4, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.4, opacity: 0 }}
          transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}>
          <motion.div
            style={{
              position: "absolute",
              borderRadius: "50%",
              background: isPlaying
                ? "radial-gradient(circle, rgba(239,68,68,0.18) 0%, transparent 70%)"
                : "radial-gradient(circle, rgba(0,214,143,0.18) 0%, transparent 70%)",
              width: 160,
              height: 160,
              left: "50%",
              top: "50%",
              x: "-50%",
              y: "-50%",
              pointerEvents: "none",
            }}
            animate={{ scale: [1, 1.18, 1], opacity: [0.7, 0.35, 0.7] }}
            transition={{ duration: 2.8, repeat: Infinity, ease: "easeInOut" }}
          />
          <motion.div
            style={{
              position: "absolute",
              borderRadius: "50%",
              border: isPlaying
                ? "1.5px solid rgba(239,68,68,0.45)"
                : "1.5px solid rgba(0,214,143,0.45)",
              width: 92,
              height: 92,
              left: "50%",
              top: "50%",
              x: "-50%",
              y: "-50%",
              pointerEvents: "none",
            }}
            animate={{ scale: [1, 1.22, 1], opacity: [0.5, 0.15, 0.5] }}
            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
          />
          <motion.div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 14,
              background: isPlaying ? "rgba(20,5,5,0.92)" : "rgba(4,20,16,0.92)",
              backdropFilter: "blur(20px)",
              WebkitBackdropFilter: "blur(20px)",
              border: isPlaying
                ? "1.5px solid rgba(239,68,68,0.55)"
                : "1.5px solid rgba(0,214,143,0.5)",
              borderRadius: 60,
              padding: "13px 26px 13px 14px",
              boxShadow: isPlaying
                ? "0 0 0 1px rgba(239,68,68,0.12), 0 8px 48px rgba(239,68,68,0.35), inset 0 1px 0 rgba(255,255,255,0.06)"
                : "0 0 0 1px rgba(0,214,143,0.12), 0 8px 48px rgba(0,214,143,0.35), inset 0 1px 0 rgba(255,255,255,0.06)",
              whiteSpace: "nowrap",
            }}
            layout
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}>
            <motion.div
              style={{
                width: 44,
                height: 44,
                borderRadius: "50%",
                background: isPlaying
                  ? "linear-gradient(135deg,#ef4444,#b91c1c)"
                  : "linear-gradient(135deg,#00d68f,#059669)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 18,
                paddingLeft: isPlaying ? 0 : 3,
                flexShrink: 0,
              }}
              animate={
                !isPlaying
                  ? {
                      boxShadow: [
                        "0 0 0 6px rgba(0,214,143,0.1), 0 4px 16px rgba(0,214,143,0.4)",
                        "0 0 0 10px rgba(0,214,143,0.18), 0 4px 28px rgba(0,214,143,0.75)",
                        "0 0 0 6px rgba(0,214,143,0.1), 0 4px 16px rgba(0,214,143,0.4)",
                      ],
                    }
                  : {
                      boxShadow: [
                        "0 0 0 6px rgba(239,68,68,0.1), 0 4px 16px rgba(239,68,68,0.4)",
                        "0 0 0 10px rgba(239,68,68,0.18), 0 4px 28px rgba(239,68,68,0.75)",
                        "0 0 0 6px rgba(239,68,68,0.1), 0 4px 16px rgba(239,68,68,0.4)",
                      ],
                    }
              }
              transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}>
              <span
                style={{
                  color: "#f8fafc",
                  lineHeight: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}>
                {isPlaying ? <FaPause size={16} /> : <FaPlay size={16} />}
              </span>
            </motion.div>
            <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
              <span
                style={{
                  fontFamily: F,
                  fontWeight: 800,
                  fontSize: 16,
                  letterSpacing: "-0.01em",
                  lineHeight: 1.1,
                  color: isPlaying ? "#fca5a5" : "#00d68f",
                }}>
                {isPlaying ? "Pause" : "Play Video"}
              </span>
              <span
                style={{
                  fontFamily: MONO,
                  fontWeight: 500,
                  fontSize: 10,
                  letterSpacing: "0.1em",
                  lineHeight: 1,
                  textTransform: "uppercase",
                  color: isPlaying ? "rgba(252,165,165,0.55)" : "rgba(0,214,143,0.5)",
                }}>
                {isPlaying ? "click to stop" : "vetassist"}
              </span>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

// ── Main Component ─────────────────────────────────────────────────────────────
const VideoShowcase = () => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [hasEntered, setHasEntered] = useState(false);
  const [isHoveringVideo, setIsHoveringVideo] = useState(false);
  const [supportsHover, setSupportsHover] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);

  const videoRef = useRef(null);
  const sectionRef = useRef(null);
  const containerRef = useRef(null);
  const videoAreaRef = useRef(null);

  const isInView = useInView(sectionRef, { once: true, margin: "-80px" });

  const steps = ["Navigate", "Select Owner", "Choose Service", "Pick Date & Time", "Confirm"];

  // Detect mobile
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return;
    const mediaQuery = window.matchMedia("(hover: hover) and (pointer: fine)");
    const updateHoverSupport = () => setSupportsHover(mediaQuery.matches);
    updateHoverSupport();
    if (typeof mediaQuery.addEventListener === "function") {
      mediaQuery.addEventListener("change", updateHoverSupport);
      return () => mediaQuery.removeEventListener("change", updateHoverSupport);
    }
    mediaQuery.addListener(updateHoverSupport);
    return () => mediaQuery.removeListener(updateHoverSupport);
  }, []);

  useEffect(() => {
    if (isInView && !hasEntered) setHasEntered(true);
  }, [isInView]);

  // 3D scroll effect
  const { scrollYProgress: scroll3D } = useScroll({
    target: sectionRef,
    offset: ["start 90%", "start 20%"],
  });
  const rotateX = useTransform(scroll3D, [0, 1], [28, 0]);
  const scale3D = useTransform(scroll3D, [0, 1], [0.78, 1]);
  const y3D = useTransform(scroll3D, [0, 1], [80, 0]);
  const opacity3D = useTransform(scroll3D, [0, 0.2, 1], [0, 1, 1]);
  const sRotateX = useSpring(rotateX, { stiffness: 50, damping: 18 });
  const sScale = useSpring(scale3D, { stiffness: 50, damping: 18 });
  const sY = useSpring(y3D, { stiffness: 50, damping: 18 });
  const sOpacity = useSpring(opacity3D, { stiffness: 50, damping: 18 });

  function handleTimeUpdate() {
    const vid = videoRef.current;
    if (!vid) return;
    const pct = vid.currentTime / vid.duration;
    if (pct < 0.15) setCurrentStep(0);
    else if (pct < 0.35) setCurrentStep(1);
    else if (pct < 0.55) setCurrentStep(2);
    else if (pct < 0.78) setCurrentStep(3);
    else setCurrentStep(4);
  }

  function togglePlay() {
    const vid = videoRef.current;
    if (!vid) return;
    vid.paused ? vid.play() : vid.pause();
  }

  return (
    <div
      ref={sectionRef}
      style={{
        fontFamily: F,
        position: "relative",
        perspective: "1200px",
        background: "#020b16",
        minHeight: "100svh",
        width: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        overflowX: "hidden",
        padding: isMobile
          ? "clamp(32px, 6vh, 56px) 16px clamp(24px, 4vh, 40px)"
          : "clamp(48px, 7vh, 80px) clamp(16px, 2vw, 24px) clamp(18px, 3.8vh, 44px)",
      }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700;800;900&family=JetBrains+Mono:wght@400;500&display=swap');
      `}</style>

      {/* Background */}
      {/* <HomeAuroraBackground showStars={true} showWavingLines={true} /> */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          pointerEvents: "none",
          zIndex: 1,
          background:
            "radial-gradient(1300px 520px at 50% -8%, rgba(18,67,90,0.62) 0%, rgba(3,12,24,0.2) 45%, rgba(2,11,22,0) 70%), radial-gradient(800px 380px at 50% 94%, rgba(2,130,110,0.26) 0%, rgba(2,11,22,0) 68%)",
        }}
      />
      <div
        style={{
          position: "absolute",
          inset: 0,
          pointerEvents: "none",
          zIndex: 2,
          background:
            "linear-gradient(180deg, rgba(2,11,22,0.2) 0%, rgba(2,11,22,0.72) 100%), radial-gradient(circle at center, transparent 42%, rgba(1,7,14,0.6) 100%)",
        }}
      />

      {/* Custom cursor (desktop only) */}
      {supportsHover && !isMobile && (
        <VideoCursor
          mouseX={mouseX}
          mouseY={mouseY}
          isHovering={isHoveringVideo}
          isPlaying={isPlaying}
        />
      )}

      {/* Ambient particles */}
      {[
        {
          width: 6,
          height: 6,
          background: "#00d68f",
          top: "12%",
          left: "10%",
          duration: 5,
          delay: 0,
        },
        {
          width: 4,
          height: 4,
          background: "#3b82f6",
          top: "55%",
          left: "7%",
          duration: 7,
          delay: 1,
        },
        {
          width: 5,
          height: 5,
          background: "#00d68f",
          top: "30%",
          right: "9%",
          duration: 6,
          delay: 0.5,
        },
        {
          width: 3,
          height: 3,
          background: "#fff",
          top: "70%",
          right: "13%",
          duration: 4,
          delay: 2,
        },
        {
          width: 7,
          height: 7,
          background: "#3b82f6",
          top: "18%",
          right: "22%",
          duration: 8,
          delay: 1.5,
        },
      ].map((p, i) => (
        <Particle key={i} style={p} />
      ))}

      {/* Content */}
      <div
        style={{
          position: "relative",
          zIndex: 10,
          width: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100%",
          pointerEvents: "auto",
        }}>
        {/* Title */}
        <motion.div
          style={{
            textAlign: "center",
            marginBottom: isMobile ? "clamp(8px, 1.4vh, 14px)" : "clamp(10px, 1.8vh, 20px)",
            marginTop: isMobile ? "clamp(8px, 1.2vh, 20px)" : "clamp(20px, 2vh, 48px)",
            position: "relative",
            zIndex: 10,
          }}
          initial={{ opacity: 0, y: 40 }}
          animate={hasEntered ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}>
          <div style={{ overflow: "hidden" }}>
            <motion.h2
              style={{
                fontFamily: F,
                fontWeight: 900,
                fontSize: isMobile ? "clamp(1.6rem, 7vw, 2.2rem)" : "clamp(1.9rem, 4.4vw, 3.3rem)",
                color: "#f8fafc",
                letterSpacing: "-.04em",
                margin: "0 0 10px",
                lineHeight: 1.05,
              }}
              initial={{ y: 60, opacity: 0 }}
              animate={hasEntered ? { y: 0, opacity: 1 } : {}}
              transition={{ duration: 0.65, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}>
              Book an Appointment{" "}
              <motion.span
                style={{
                  background: "linear-gradient(130deg,#00d68f,#3b82f6)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  display: "inline-block",
                }}
                initial={{ opacity: 0, x: -20 }}
                animate={hasEntered ? { opacity: 1, x: 0 } : {}}
                transition={{ duration: 0.6, delay: 0.35 }}>
                in Seconds
              </motion.span>
            </motion.h2>
          </div>
          <motion.p
            style={{
              color: "#9fb2c8",
              fontSize: isMobile ? 12 : 13,
              fontFamily: F,
              margin: 0,
            }}
            initial={{ opacity: 0 }}
            animate={hasEntered ? { opacity: 1 } : {}}
            transition={{ duration: 0.6, delay: 0.45 }}>
            Watch how easy it is to schedule a visit on VetAssist
          </motion.p>
        </motion.div>

        {/* Steps — mobile shows single animated step, desktop shows all pills */}
        <div
          style={{
            marginBottom: isMobile ? "clamp(8px, 1.2vh, 14px)" : "clamp(10px, 1.5vh, 18px)",
            width: "100%",
            display: "flex",
            justifyContent: "center",
            padding: isMobile ? "0 12px" : "0",
          }}>
          {isMobile ? (
            <MobileStepDisplay steps={steps} current={currentStep} isVisible={hasEntered} />
          ) : (
            <StepPill steps={steps} current={currentStep} isVisible={hasEntered} />
          )}
        </div>

        {/* Browser card */}
        <div
          ref={containerRef}
          style={{
            position: "relative",
            width: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: isMobile ? "0" : "0 2.5rem",
          }}>
          <motion.div
            style={{
              width: isMobile
                ? "100%"
                : "min(100%, 74rem, max(20rem, calc((100dvh - 16rem) * 1.7778)))",
              borderRadius: isMobile ? 14 : 18,
              padding: 3,
              background:
                "linear-gradient(135deg,rgba(0,214,143,0.5),rgba(59,130,246,0.5),rgba(0,214,143,0.2))",
              boxShadow:
                "0 0 0 1px rgba(0,214,143,0.2), 0 40px 100px rgba(0,0,0,0.8), 0 0 60px rgba(0,214,143,0.15)",
              rotateX: isMobile ? 0 : sRotateX,
              scale: isMobile ? 1 : sScale,
              y: isMobile ? 0 : sY,
              opacity: isMobile ? 1 : sOpacity,
              transformOrigin: "bottom center",
              transformStyle: "preserve-3d",
            }}
            whileHover={
              !isMobile
                ? {
                    boxShadow:
                      "0 0 0 1px rgba(0,214,143,0.4), 0 50px 120px rgba(0,0,0,0.9), 0 0 80px rgba(0,214,143,0.25)",
                  }
                : {}
            }>
            <div style={{ borderRadius: isMobile ? 11 : 15, overflow: "hidden" }}>
              {/* Browser bar */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  height: isMobile ? 40 : 56,
                  background: "#0f0f1c",
                  borderRadius: `${isMobile ? 11 : 15}px ${isMobile ? 11 : 15}px 0 0`,
                  border: "1px solid rgba(255,255,255,0.1)",
                  borderBottom: "none",
                  padding: isMobile ? "0 12px" : "0 20px",
                }}>
                <div style={{ display: "flex", alignItems: "center", gap: isMobile ? 12 : 24 }}>
                  {/* Mac dots */}
                  <div style={{ display: "flex", gap: isMobile ? 5 : 8 }}>
                    {["#ff5f56", "#ffbd2e", "#00d68f"].map((bg, i) => (
                      <motion.div
                        key={i}
                        style={{
                          width: isMobile ? 8 : 11,
                          height: isMobile ? 8 : 11,
                          borderRadius: "50%",
                          background: bg,
                          opacity: 0.85,
                        }}
                        whileHover={{ scale: 1.3, opacity: 1 }}
                      />
                    ))}
                  </div>
                  {/* Tab label */}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: isMobile ? 5 : 8,
                      borderRadius: 6,
                      padding: isMobile ? "4px 8px" : "6px 12px",
                      background: "rgba(0,214,143,0.15)",
                      color: "#00d68f",
                      fontFamily: F,
                      fontSize: isMobile ? 11 : 14,
                      fontWeight: 600,
                    }}>
                    <svg
                      style={{ width: isMobile ? 12 : 16, height: isMobile ? 12 : 16 }}
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                      />
                    </svg>
                    Appointments
                  </div>
                </div>
                {/* URL bar — hidden on mobile */}
                {!isMobile && (
                  <div
                    style={{
                      background: "rgba(255,255,255,0.05)",
                      borderRadius: 5,
                      padding: "3px 14px",
                      fontSize: 10,
                      color: "#a8bed8",
                      fontFamily: MONO,
                    }}>
                    app.vetassist.ph/appointments
                  </div>
                )}
              </div>

              {/* Video area */}
              <div
                ref={videoAreaRef}
                style={{
                  background: "#000",
                  borderRadius: `0 0 ${isMobile ? 11 : 15}px ${isMobile ? 11 : 15}px`,
                  overflow: "hidden",
                  position: "relative",
                  aspectRatio: "16/9",
                  cursor: supportsHover && !isMobile ? "none" : "pointer",
                }}
                onClick={togglePlay}
                onMouseMove={(e) => {
                  if (!supportsHover || isMobile) return;
                  mouseX.set(e.clientX);
                  mouseY.set(e.clientY);
                }}
                onMouseEnter={(e) => {
                  if (!supportsHover || isMobile) return;
                  mouseX.set(e.clientX);
                  mouseY.set(e.clientY);
                  setIsHoveringVideo(true);
                }}
                onMouseLeave={() => setIsHoveringVideo(false)}>
                <video
                  ref={videoRef}
                  src={appointmentVideo}
                  style={{ width: "100%", height: "100%", objectFit: "contain", display: "block" }}
                  onTimeUpdate={handleTimeUpdate}
                  onPlay={() => setIsPlaying(true)}
                  onPause={() => setIsPlaying(false)}
                  onEnded={() => {
                    setIsPlaying(false);
                    setCurrentStep(0);
                  }}
                  playsInline
                />

                {/* Paused overlay */}
                <AnimatePresence>
                  {!isPlaying && (
                    <motion.div
                      style={{
                        position: "absolute",
                        inset: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        background: "rgba(0,0,0,0.30)",
                        pointerEvents: "none",
                      }}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.2 }}>
                      {(!isHoveringVideo || isMobile) && (
                        <motion.div
                          style={{
                            width: isMobile ? 56 : 72,
                            height: isMobile ? 56 : 72,
                            borderRadius: "50%",
                            background: "linear-gradient(135deg,#00d68f,#059669)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            paddingLeft: 5,
                            boxShadow: "0 8px 40px rgba(0,214,143,0.6)",
                          }}
                          initial={{ scale: 0.5, opacity: 0 }}
                          animate={{ scale: 1, opacity: 1 }}
                          exit={{ scale: 0.5, opacity: 0 }}
                          transition={{ type: "spring", stiffness: 300, damping: 20 }}>
                          <FaPlay
                            size={isMobile ? 16 : 22}
                            color="#f8fafc"
                            style={{ marginLeft: 4 }}
                          />
                        </motion.div>
                      )}
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Shimmer on load */}
                <motion.div
                  style={{
                    pointerEvents: "none",
                    position: "absolute",
                    inset: 0,
                    zIndex: 30,
                    background:
                      "linear-gradient(to top right, transparent, rgba(255,255,255,0.05), transparent)",
                  }}
                  initial={{ x: "-100%" }}
                  whileInView={{ x: "200%" }}
                  viewport={{ once: true }}
                  transition={{ duration: 1.5, delay: 0.5, ease: "easeInOut" }}
                />

                {/* Hover glow */}
                <AnimatePresence>
                  {isHoveringVideo && !isMobile && (
                    <motion.div
                      style={{
                        position: "absolute",
                        inset: 0,
                        borderRadius: `0 0 ${isMobile ? 11 : 15}px ${isMobile ? 11 : 15}px`,
                        pointerEvents: "none",
                        boxShadow: "inset 0 0 60px rgba(0,214,143,0.07)",
                      }}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.3 }}
                    />
                  )}
                </AnimatePresence>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default VideoShowcase;
